'use client';

import { useEffect, useRef, useState } from 'react';
import { fetchNextBestActions } from '@/services/microservices/nextBestAction';
import { getMicroservicesCountryCode } from '@/services/microservices/usedCarEligibility';
import { getDocumentLanguage } from '@/utils/documentLanguage';
import { MappedAlerts, nbaToAlertItems } from './nbaToAlertItems';
import { SnapshotAuthState } from './SnapshotUser.types';
import { getClientSdk } from './clientSdk';

export const useSnapshotAlerts = (authState: SnapshotAuthState): MappedAlerts | null => {
    const [mappedAlerts, setMappedAlerts] = useState<MappedAlerts | null>(null);
    const loadGenerationRef = useRef(0);

    useEffect(() => {
        const generation = ++loadGenerationRef.current;
        const isStale = () => generation !== loadGenerationRef.current;

        const loadAlerts = async () => {
            if (authState !== 'authenticated') {
                setMappedAlerts(null);
                return;
            }

            const clientSdk = await getClientSdk();

            if (!clientSdk || isStale()) {
                return;
            }

            try {
                const countryCode = getMicroservicesCountryCode();
                const nbaResponse = await fetchNextBestActions(countryCode, {
                    locale: getDocumentLanguage() || 'en-US',
                });
                const actions = nbaResponse.data?.actions ?? [];

                if (isStale()) {
                    return;
                }

                setMappedAlerts(nbaToAlertItems(actions));
            } catch (error) {
                console.warn('[snapshot-alert] NBA call failed; leaving alerts empty', error);
            }
        };

        void loadAlerts();

        return () => {
            loadGenerationRef.current += 1;
        };
    }, [authState]);

    return mappedAlerts;
};
